import {
  AuthInputTextField,
  AuthSeparator,
  EmailTagInput,
  Tag,
  TextButton,
} from '@client/components';
import { OAuthConnection } from '@client/components/OauthConnection';
import React from 'react';
import RadioSelect from '../RadioSelectOptions';
import { AdvisorField } from './AdvisorField';
import { EmailSignatureEditor } from './EmailSignatureEditor';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { useHasCustomEmailSignature } from '@/shared/hooks/use-custom-email-signature';
import {
  useActivePilotProgram,
  useIsPilotRestricted,
} from '@/shared/hooks/use-active-pilot-program';
import { useIsGenworthMode } from '@/features/genworth/hooks/use-genworth-mode';
import { getLinkWithProtocol } from '@client/lib/utils';
const clientIntakeGuideRadioOptions = [
  {
    label: 'Do Not Send a Guide',
    description:
      'Clients will not receive any educational guide email after completing their intake form.',
    value: 'none',
    id: 'intake-guide-none',
  },
  {
    label: 'Send "Advisor-Led Planning" Guide',
    description:
      "Send clients a guide about what to expect from their upcoming results review with you — including what you'll cover together and how to prepare for the conversation.",
    value: 'advisorLed',
    id: 'intake-guide-advisor-led',
  },
  {
    label: 'Send "Self-Guided Planning" Guide',
    description:
      'Send clients a guide designed to help them independently understand their results — including how to read each section and what the numbers mean for their planning.',
    value: 'selfGuided',
    id: 'intake-guide-self-guided',
  },
];

type ClientIntakeGuideOption = (typeof clientIntakeGuideRadioOptions)[number] & {
  disabled?: boolean;
};

export function getClientIntakeGuideSettings(
  isNylPilot: boolean,
  selectedValue?: ClientIntakeGuideType,
): {
  options: ClientIntakeGuideOption[];
  selectedValue: ClientIntakeGuideType;
  isReadOnly: boolean;
} {
  return {
    options: isNylPilot
      ? clientIntakeGuideRadioOptions.map(option => ({
          ...option,
          disabled: option.value !== 'none',
        }))
      : clientIntakeGuideRadioOptions,
    selectedValue: isNylPilot ? 'none' : selectedValue || 'advisorLed',
    isReadOnly: isNylPilot,
  };
}

interface CommunicationTabProps {
  advisorState: AdvisorProfile;
  errors: Partial<Record<keyof AdvisorProfile, boolean>>;
  maxSchedulingLinkDisplayTextLength: number;
  handleAdvisorStateChange: (
    advisorKey: keyof AdvisorProfile,
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleShouldSendClientEmailChange: (value: string | number) => void;
  handleEmailSignatureChange: (value: string) => void;
  emailTags: Tag[];
  handleEmailTagsChange: (emails: Tag[]) => void;
  complianceBCCEmailTags: Tag[];
  handleComplianceBCCEmailTagsChange: (emails: Tag[]) => void;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
  setAdvisorState: React.Dispatch<React.SetStateAction<AdvisorProfile>>;
  clearEmailWarnings: boolean;
  clearComplianceBCCEmailWarnings: boolean;
}

export const shouldSendClientEmailRadioOptions = [
  {
    label: 'Manual Sharing:',
    description:
      "You'll manually choose when and how to share results with each client. (best for clients who may get intimidated or confused without your help)",
    value: 'no',
    id: 'no-client-results',
  },
  {
    label: 'Enable Client Self-Service:',
    description:
      'Give new client(s) direct access to view their results upon invite. (best for clients who are tech-savvy self starters)',
    value: 'yes',
    id: 'yes-client-results',
  },
];

export function CommunicationTab({
  advisorState,
  errors,
  maxSchedulingLinkDisplayTextLength,
  handleAdvisorStateChange,
  handleShouldSendClientEmailChange,
  handleEmailSignatureChange,
  emailTags,
  handleEmailTagsChange,
  complianceBCCEmailTags,
  handleComplianceBCCEmailTagsChange,
  setHasUnsavedChanges,
  setAdvisorState,
  clearEmailWarnings,
  clearComplianceBCCEmailWarnings,
}: CommunicationTabProps) {
  const { flags } = useFeatureFlags();
  const hasCustomEmailSignature = useHasCustomEmailSignature();
  const isEmailConnectionRestricted = useIsPilotRestricted('email-connection');
  const isGenworthMode = useIsGenworthMode();
  const isNylPilot = useActivePilotProgram()?.flagKey === 'nylPilotAdvisor';
  const isAutomaticClientResultsRestricted = useIsPilotRestricted('automatic-client-results');
  const clientResultsOptions = isAutomaticClientResultsRestricted
    ? shouldSendClientEmailRadioOptions.map(option => ({ ...option, disabled: true }))
    : shouldSendClientEmailRadioOptions;
  const clientIntakeGuideSettings = getClientIntakeGuideSettings(
    isNylPilot,
    advisorState.sendClientIntakeGuide,
  );

  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
      {!isEmailConnectionRestricted && (
        <>
          <AuthSeparator
            className="sm:col-span-6"
            text="Email Connection Settings"
          />
          <AdvisorField
            label="Connect Your Email Account (Beta)"
            description="Send personalized emails directly from your email account to strengthen client relationships and improve client engagement."
            tooltip="Connecting your email account lets Waterlily send important emails directly from your email address. This includes communications with clients about their plans, meeting details, and automated reminders. Only purely transactional emails will come from the Waterlily system."
          >
            <div className="flex flex-col gap-4 pt-2">
              <OAuthConnection provider="google" />
              <OAuthConnection provider="microsoft" />
            </div>
          </AdvisorField>
        </>
      )}

      {hasCustomEmailSignature && (
        <>
          <AuthSeparator
            className="sm:col-span-6"
            text="Email Signature"
          />
          <div className="sm:col-span-6">
            <EmailSignatureEditor
              id="emailSignature"
              label="Your Email Signature"
              description="Used in place of the default sign-off in emails Waterlily sends to your clients on your behalf, such as intake invitations."
              value={advisorState.emailSignature ?? ''}
              onChange={handleEmailSignatureChange}
              className="mt-0"
            />
          </div>
        </>
      )}

      <AuthSeparator
        className="sm:col-span-6"
        text="Client Booking Settings"
      />
      <AuthInputTextField
        id="schedulingLinkUrl"
        label="Scheduling Link (allow clients to book time on your calendar)"
        value={advisorState.schedulingLinkUrl ?? ''}
        onChange={handleAdvisorStateChange('schedulingLinkUrl')}
        required={false}
        error={errors.schedulingLinkUrl}
        className="mt-0 sm:col-span-4"
      />
      {!errors.schedulingLinkUrl && (
        <TextButton
          buttonLabel="Open link to test"
          href={getLinkWithProtocol(advisorState.schedulingLinkUrl)}
          className="-mt-5 ml-1 text-sm sm:col-span-4"
        />
      )}
      <AuthInputTextField
        id="schedulingLinkDisplayText"
        label={`Scheduling Link Button Text (max ${maxSchedulingLinkDisplayTextLength} characters)`}
        value={advisorState.schedulingLinkDisplayText ?? ''}
        onChange={handleAdvisorStateChange('schedulingLinkDisplayText')}
        required={false}
        error={errors.schedulingLinkDisplayText}
        className="mt-0 sm:col-span-4"
        placeholder="Connect with an advisor"
      />

      {!isGenworthMode && (
        <>
          <AuthSeparator
            className="sm:col-span-6"
            text="Client Results Sharing"
          />
          <AdvisorField label="Client Results Settings">
            <RadioSelect
              options={clientResultsOptions}
              selectedValue={
                !isAutomaticClientResultsRestricted && advisorState.sendClientResultsLinkSetting
                  ? 'yes'
                  : 'no'
              }
              onChange={handleShouldSendClientEmailChange}
              className="tracking-normal"
              variant="box"
            />
          </AdvisorField>

          <AuthSeparator
            className="sm:col-span-6"
            text="Client Education Guide"
          />
          <AdvisorField
            label="Send Education Guide on Intake Completion"
            description="Choose which educational guide to email clients after they complete their intake form, or disable the email entirely."
          >
            <RadioSelect
              options={clientIntakeGuideSettings.options}
              selectedValue={clientIntakeGuideSettings.selectedValue}
              onChange={(value: string | number) => {
                if (clientIntakeGuideSettings.isReadOnly) return;
                setAdvisorState(prevState => ({
                  ...prevState,
                  sendClientIntakeGuide: value as ClientIntakeGuideType,
                }));
                setHasUnsavedChanges(true);
              }}
              className="tracking-normal"
              variant="box"
            />
          </AdvisorField>
        </>
      )}

      {flags.complianceBCCEmails && (
        <>
          <AuthSeparator
            className="sm:col-span-6"
            text="Compliance & Auditing"
          />
          <AdvisorField
            label="Blind carbon copy (BCC) an email address on all client communications"
            description="These emails will be automatically BCC'd on ALL communications sent to your clients (results, intake forms, reminders, consent requests, etc.). Used for compliance auditing and record-keeping purposes."
            tooltip="Every email sent to your clients will include these addresses in BCC. This is separate from the intake form notification setting below, which only applies to advisor notifications about form submissions."
          >
            <EmailTagInput
              onChange={emails => {
                handleComplianceBCCEmailTagsChange(emails);
                setHasUnsavedChanges(true);
                setAdvisorState(prevState => ({
                  ...prevState,
                  complianceBCCEmailsForClientCommunications: emails
                    .filter(email => email.isValid)
                    .map(email => email.value),
                }));
              }}
              initialEmails={complianceBCCEmailTags}
              maxEmails={10}
              clearWarnings={clearComplianceBCCEmailWarnings}
              label="Emails to be BCC'd"
            />
          </AdvisorField>
        </>
      )}

      <AuthSeparator
        className="sm:col-span-6"
        text="Advisor Notifications"
      />
      <AdvisorField
        label="Additional Recipients for Intake Form Submissions"
        description="These emails will be CC'd when you receive notifications about new client intake form submissions (advisor-facing notifications only)."
      >
        <EmailTagInput
          onChange={emails => {
            handleEmailTagsChange(emails);
            setHasUnsavedChanges(true);
            setAdvisorState(prevState => ({
              ...prevState,
              additionalRecipientsForIntakeFormSubmissions: emails
                .filter(email => email.isValid)
                .map(email => email.value),
            }));
          }}
          initialEmails={emailTags}
          maxEmails={50}
          clearWarnings={clearEmailWarnings}
        />
      </AdvisorField>
    </div>
  );
}
